import type { HTMLAttributes, ReactElement } from 'react';
import { cn } from '../../lib/cn';

export type BadgeVariant = 'success' | 'danger' | 'warning' | 'info' | 'muted';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

const variantClass: Record<BadgeVariant, string> = {
  success: 'bg-success/15 text-success border-success/30',
  danger: 'bg-danger/15 text-danger border-danger/30',
  warning: 'bg-warning/15 text-warning border-warning/30',
  info: 'bg-info/15 text-info border-info/30',
  muted: 'bg-bg-hover text-muted border-border',
};

/**
 * Inline badge — replaces the legacy `statusBadge` helper from `app.js`.
 *
 * Five semantic variants tinted from the design tokens. Renders as a
 * `<span>`, so it can be embedded inline (e.g. inside a `<td>`).
 */
export function Badge({ variant = 'muted', className, ...props }: BadgeProps): ReactElement {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        variantClass[variant],
        className,
      )}
      {...props}
    />
  );
}

/**
 * Status keys we consume from the backend. Extend here as the API grows.
 *
 * Order-status values mirror `OrderStatusEnum.java`
 * (`CREATED, ACCEPTED, PREPARING, READY, COURIER_ASSIGNED, ON_THE_WAY,
 * DELIVERED, CANCELLED`). Delivery-status values mirror
 * `DeliveryStatusEnum.java` (`ASSIGNED, PICKED_UP, ON_THE_WAY, DELIVERED,
 * CANCELLED`). `DISPATCHED` is kept for back-compat with
 * `features/courier-ops` which still maps both `PICKED_UP` and `ON_THE_WAY`
 * to a single legacy "DISPATCHED" badge.
 */
export type StatusKey =
  | 'CREATED'
  | 'PENDING'
  | 'ACCEPTED'
  | 'PREPARING'
  | 'READY'
  | 'COURIER_ASSIGNED'
  | 'ON_THE_WAY'
  | 'DISPATCHED'
  | 'ASSIGNED'
  | 'PICKED_UP'
  | 'DELIVERED'
  | 'CANCELLED'
  | 'REJECTED'
  | 'APPROVED'
  | 'NOT_REQUIRED'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'SUSPENDED'
  | 'PAID'
  | 'REFUNDED'
  | 'FAILED';

const statusToVariant: Record<StatusKey, BadgeVariant> = {
  CREATED: 'info',
  PENDING: 'warning',
  ACCEPTED: 'info',
  PREPARING: 'info',
  READY: 'info',
  COURIER_ASSIGNED: 'info',
  ON_THE_WAY: 'info',
  DISPATCHED: 'info',
  ASSIGNED: 'info',
  PICKED_UP: 'info',
  DELIVERED: 'success',
  APPROVED: 'success',
  ACTIVE: 'success',
  PAID: 'success',
  CANCELLED: 'danger',
  REJECTED: 'danger',
  FAILED: 'danger',
  SUSPENDED: 'warning',
  REFUNDED: 'warning',
  INACTIVE: 'muted',
  NOT_REQUIRED: 'muted',
};

const statusToLabel: Record<StatusKey, string> = {
  CREATED: 'Criado',
  PENDING: 'Pendente',
  ACCEPTED: 'Aceito',
  PREPARING: 'Preparando',
  READY: 'Pronto',
  COURIER_ASSIGNED: 'Entregador atribuído',
  ON_THE_WAY: 'Em rota',
  DISPATCHED: 'Em rota',
  ASSIGNED: 'Atribuída',
  PICKED_UP: 'Coletada',
  DELIVERED: 'Entregue',
  CANCELLED: 'Cancelado',
  REJECTED: 'Rejeitado',
  APPROVED: 'Aprovado',
  NOT_REQUIRED: 'N/A',
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  SUSPENDED: 'Suspenso',
  PAID: 'Pago',
  REFUNDED: 'Estornado',
  FAILED: 'Falhou',
};

/**
 * Translate a raw backend status string to its PT-BR label. Unknown values
 * fall back to the input untouched, matching `<StatusBadge>` semantics.
 */
export function formatOrderStatus(status: string | null | undefined): string {
  if (status == null) return '—';
  return statusToLabel[status as StatusKey] ?? status;
}

/** Mirrors the backend's two-value delivery-type domain (`STANDARD | DOOR`). */
const deliveryTypeToLabel: Record<string, string> = {
  STANDARD: 'Padrão',
  DOOR: 'Na porta',
};

/**
 * Translate a delivery-type enum into PT-BR. Backed by the two-value union
 * the backend currently emits (`OrderDetailResponse.deliveryType`); unknown
 * values pass through unchanged.
 */
export function formatDeliveryType(type: string | null | undefined): string {
  if (type == null) return '—';
  return deliveryTypeToLabel[type] ?? type;
}

export interface StatusBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Status key returned by the backend. Unknown values fall back to `muted`. */
  status: StatusKey | string;
  /** Override the rendered label (falls back to a translated default). */
  label?: string;
}

/**
 * `<StatusBadge status="DELIVERED">` — convenience wrapper that maps the
 * common backend status keys to the right `Badge` variant + a PT-BR label.
 */
export function StatusBadge({ status, label, className, ...rest }: StatusBadgeProps): ReactElement {
  const key = status as StatusKey;
  const variant = statusToVariant[key] ?? 'muted';
  const text = label ?? statusToLabel[key] ?? status;
  return (
    <Badge variant={variant} className={className} {...rest}>
      {text}
    </Badge>
  );
}
